import type { BuyerSubmissionDetailDto } from "@/api/buyers";
import type { TabKey } from "./submissionTabs";

/**
 * What to do next on this submission, in one sentence.
 *
 * <p>🔴 <b>Derived, never stored.</b> Nothing writes a "next action" and nothing
 * reads this back — it is a reading of the status the record already carries,
 * exactly like the phase ladder. A persisted next-step field would immediately
 * become a second account of where the submission stands, and the two would
 * disagree the first time a status moved without anyone updating it.</p>
 *
 * <p>The overdue follow-up OUTRANKS the status, because it is the only fact here
 * with a date attached that has already passed. Everything else is "this is the
 * rung you are on"; an overdue follow-up is "you said you would do something and
 * the day has been and gone".</p>
 *
 * <p><c>isFollowUpOverdue</c> and <c>nextFollowUpDate</c> are still the fields
 * this reads, but they no longer come from a column on the submission — the
 * server derives them from the submission's OPEN TASKS. The question is the same
 * one; only its source moved.</p>
 *
 * <p>An unrecognised status degrades to the generic prompt rather than throwing
 * or claiming the submission is finished — the same defaulting rule the phase
 * ladder uses, and for the same reason: a status added server-side must not make
 * a page render a falsehood before the frontend catches up.</p>
 */
export interface NextAction {
  headline: string;
  detail: string;
  /** The tab that carries the control, when one does. */
  tab?: TabKey;
  /** The label for the jump, when there is a tab to jump to. */
  cta?: string;
  /** Overdue work reads as a warning; ordinary progress does not. */
  tone: "warn" | "neutral";
}

export function nextActionFor(s: BuyerSubmissionDetailDto): NextAction {
  // Terminal states first: there is no next action, and inventing one for a
  // submission that has ended is how a specialist wastes an afternoon.
  if (s.status === "EscalatedToDeal") {
    return {
      headline: "This is a deal now",
      detail:
        "The submission is settled on this side. Its progress is read through "
        + "the deal below, and the work happens there.",
      tone: "neutral",
    };
  }

  const ended = ENDED.get(s.status);
  if (ended) return { headline: ended, detail: CLOSED_OUT, tone: "neutral" };

  // 🔴 Ranked above the status: the status says which rung; an overdue
  // follow-up says a promise has already been missed.
  //
  // The follow-up is a TASK now, so the jump goes to the tab that can actually
  // settle it. It used to send the reader to the conversation log, which was the
  // closest thing available when the follow-up was a bare date — but logging a
  // call never cleared that date, so the prompt reappeared on the next load and
  // the only way to silence it was to remember to blank a field on the offer
  // form. Closing the task is what makes this go away, and the Tasks tab is
  // where that button is.
  if (s.isFollowUpOverdue && s.nextFollowUpDate) {
    return {
      headline: "The follow-up is overdue",
      detail:
        "A task on this submission was due already. Work it and close it, or "
        + "move its due date so the queue tells the truth.",
      tab: "tasks",
      cta: "Open the tasks",
      tone: "warn",
    };
  }

  if (s.status === "CallMeeting") return dealStructureAction(s);

  return BY_STATUS[s.status] ?? GENERIC;
}

/**
 * After the call: the Potential Deal Structure.
 *
 * <p>🔴 <b>Keyed ONLY on `dealStructureCompletedAtUtc` and
 * `dealStructureStep`.</b> Never on `offerWizardCompletedAtUtc`, which was
 * backfilled on every historical row — reading it would tell every old
 * submission its structure was done.</p>
 */
function dealStructureAction(s: BuyerSubmissionDetailDto): NextAction {
  if (s.dealStructureCompletedAtUtc) {
    return {
      headline: "The deal structure is ready",
      detail:
        "Generate the Potential Deal Structure on the package and send it to the "
        + "listing broker.",
      tab: "package",
      cta: "Open the package",
      tone: "neutral",
    };
  }
  const started = s.dealStructureStep != null;
  return {
    headline: started ? "Finish the deal structure" : "Build the deal structure",
    detail:
      "Prepare Phoenix's Potential Deal Structure from the listing and the "
      + "buyer's prequalification, so the package, the LOI and the deal agree.",
    tab: "deal-structure",
    cta: "Open the deal structure",
    tone: "neutral",
  };
}

const CLOSED_OUT =
  "Nothing further is owed on this pairing. The reason is on the status change.";

/** The five terminal statuses, worded as what happened rather than as a task. */
const ENDED = new Map<string, string>([
  ["DeclinedByBuyer", "The buyer passed on this listing"],
  ["NoResponse", "It went quiet"],
  ["RejectedByBroker", "The listing broker closed it out"],
  ["Passed", "Phoenix passed on this listing"],
  ["Dead", "This pairing is closed"],
]);

const GENERIC: NextAction = {
  headline: "Move it forward",
  detail: "Work the submission and update its status so the funnel stays honest.",
  tone: "neutral",
};

/**
 * One entry per non-terminal rung of the ladder.
 *
 * <p>Every `tab` names a tab that actually carries the control the sentence asks
 * for — a prompt that sends a reader somewhere the button is not is worse than
 * no prompt at all.</p>
 */
const BY_STATUS: Partial<Record<BuyerSubmissionDetailDto["status"], NextAction>> = {
  Shortlisted: {
    headline: "Get the buyer's approval",
    detail:
      "Nobody outside Phoenix has been contacted yet. Send the intro email and "
      + "find out whether this is worth taking to the listing broker.",
    tab: "intro-email",
    cta: "Open the intro email",
    tone: "neutral",
  },
  BuyerApproved: {
    headline: "Approach the listing broker",
    detail: "The buyer is in. Generate the package, review it, and send it out.",
    tab: "package",
    cta: "Open the package",
    tone: "neutral",
  },
  OutreachSent: {
    headline: "Waiting on the listing broker",
    detail: "Log whatever comes back, then move the status to match.",
    tab: "communications",
    cta: "Log a communication",
    tone: "neutral",
  },
  BrokerResponded: {
    headline: "The broker has replied",
    detail: "Get the package in front of them while the conversation is live.",
    tab: "package",
    cta: "Open the package",
    tone: "neutral",
  },
  PackageGenerated: {
    headline: "The package is drafted, not sent",
    detail:
      "Generation is deliberately off the send path — read it, edit the "
      + "narrative if it needs it, then send it.",
    tab: "package",
    cta: "Review and send",
    tone: "neutral",
  },
  PackageSent: {
    headline: "Waiting on the CIM and the financials",
    detail: "Record each one as it arrives so the diligence trail is real.",
    tab: "paperwork",
    cta: "Open the paperwork",
    tone: "neutral",
  },
  InfoReceived: {
    headline: "Get the buyer in front of the seller",
    detail: "The documents are in. Book the call and log it afterwards.",
    tab: "calendar",
    cta: "Open the calendar",
    tone: "neutral",
  },
  // CallMeeting reads the deal structure's state: see `dealStructureAction`.
  OfferLoi: {
    headline: "Get the LOI signed",
    detail:
      "File the signed LOI in this submission's documents — the hand-off picks "
      + "it from there rather than from an upload.",
    tab: "documents",
    cta: "Open documents",
    tone: "neutral",
  },
  UnderContract: {
    headline: "Hand it to a Broker Liaison",
    detail:
      "This is ready to become a Phoenix deal. Resolve the seller "
      + "prequalification first if it is still outstanding.",
    tab: "escalation",
    cta: "Open the escalation check",
    tone: "neutral",
  },
  ClosedPurchased: {
    headline: "The buyer bought it",
    detail:
      "The best end this ladder has. Nothing further is owed unless Phoenix is "
      + "financing it, in which case escalate it to a deal.",
    tone: "neutral",
  },
};
